import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import type { AudienceFilter } from "@/lib/campaigns/audience-query";

export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "dispatching"
  | "completed"
  | "failed"
  | "cancelled";

export interface Campaign {
  id: string;
  clinicId: string;
  name: string;
  description: string | null;
  templateId: string;
  audienceFilter: AudienceFilter;
  estimatedRecipients: number | null;
  scheduledAt: string | null;
  dispatchedAt: string | null;
  status: CampaignStatus;
  failureReason: string | null;
  estimatedCostBrl: number | null;
  totalSent: number;
  totalDelivered: number;
  totalRead: number;
  totalFailed: number;
  attributedRevenue: number;
  conversions: number;
  roi: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignInput {
  name: string;
  description?: string;
  templateId: string;
  audienceFilter: AudienceFilter;
  estimatedRecipients?: number;
  scheduledAt?: string | null;
  estimatedCostBrl?: number;
}

type CampaignAttributionSummary = {
  attributedRevenue: number;
  conversions: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRow(row: any, attribution?: CampaignAttributionSummary): Campaign {
  const attributedRevenue = attribution?.attributedRevenue ?? 0;
  const estimatedCost = Number(row.estimated_cost_brl ?? 0);
  return {
    id: row.id,
    clinicId: row.clinic_id,
    name: row.name,
    description: row.description ?? null,
    templateId: row.template_id,
    audienceFilter: row.audience_filter ?? {},
    estimatedRecipients: row.estimated_recipients ?? null,
    scheduledAt: row.scheduled_at ?? null,
    dispatchedAt: row.dispatched_at ?? null,
    status: row.status as CampaignStatus,
    failureReason: row.failure_reason ?? null,
    estimatedCostBrl: row.estimated_cost_brl ?? null,
    totalSent: row.total_sent ?? 0,
    totalDelivered: row.total_delivered ?? 0,
    totalRead: row.total_read ?? 0,
    totalFailed: row.total_failed ?? 0,
    attributedRevenue,
    conversions: attribution?.conversions ?? 0,
    roi: estimatedCost > 0 ? (attributedRevenue - estimatedCost) / estimatedCost : null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const QUERY_KEY = "campaigns";

export function useCampaigns() {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: [QUERY_KEY, clinicId],
    queryFn: async (): Promise<Campaign[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("campaigns" as never)
        .select("*")
        .eq("clinic_id", clinicId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows = data as any[];
      const campaignIds = rows.map((row) => row.id as string);
      const attributionMap: Record<string, CampaignAttributionSummary> = {};

      if (campaignIds.length > 0) {
        const { data: attributions, error: attributionError } = await supabase
          .from("campaign_attribution" as never)
          .select("campaign_id, attributed_revenue")
          .eq("clinic_id", clinicId)
          .in("campaign_id", campaignIds);

        if (attributionError) throw attributionError;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((attributions ?? []) as any[]).forEach((row) => {
          const campaignId = row.campaign_id as string;
          if (!attributionMap[campaignId]) {
            attributionMap[campaignId] = { attributedRevenue: 0, conversions: 0 };
          }
          attributionMap[campaignId].attributedRevenue += Number(row.attributed_revenue ?? 0);
          attributionMap[campaignId].conversions += 1;
        });
      }

      return rows.map((row) => mapRow(row, attributionMap[row.id]));
    },
    enabled: !!clinicId,
    refetchInterval: (query) => {
      const campaigns = query.state.data;
      if (Array.isArray(campaigns) && campaigns.some((c) => c.status === "dispatching" || c.status === "scheduled"))
        return 30_000;
      return false;
    },
    staleTime: 60_000,
  });
}

export function useCreateCampaign() {
  const { data: clinicId } = useCurrentClinicId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCampaignInput) => {
      if (!clinicId) throw new Error("No clinic selected");
      const { data, error } = await supabase
        .from("campaigns" as never)
        .insert({
          clinic_id: clinicId,
          name: input.name,
          description: input.description ?? null,
          template_id: input.templateId,
          audience_filter: input.audienceFilter,
          estimated_recipients: input.estimatedRecipients ?? null,
          scheduled_at: input.scheduledAt ?? null,
          estimated_cost_brl: input.estimatedCostBrl ?? null,
          status: input.scheduledAt ? "scheduled" : "draft",
        } as never)
        .select()
        .single();
      if (error) throw error;
      return mapRow(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, clinicId] }),
  });
}

export function useDispatchCampaign() {
  const { data: clinicId } = useCurrentClinicId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (campaignId: string) => {
      // Mark campaign as dispatching — actual message sending is done server-side
      // or via Edge Function. Here we set status=dispatching and dispatched_at.
      const { data, error } = await supabase
        .from("campaigns" as never)
        .update({
          status: "dispatching",
          dispatched_at: new Date().toISOString(),
        } as never)
        .eq("id", campaignId)
        .select()
        .single();
      if (error) throw error;
      return mapRow(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, clinicId] }),
  });
}

export function useUpdateCampaign() {
  const { data: clinicId } = useCurrentClinicId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<CreateCampaignInput> }) => {
      const update: Record<string, unknown> = {};
      if (patch.name !== undefined) update.name = patch.name;
      if (patch.description !== undefined) update.description = patch.description;
      if (patch.templateId !== undefined) update.template_id = patch.templateId;
      if (patch.audienceFilter !== undefined) update.audience_filter = patch.audienceFilter;
      if (patch.estimatedRecipients !== undefined) update.estimated_recipients = patch.estimatedRecipients;
      if (patch.scheduledAt !== undefined) {
        update.scheduled_at = patch.scheduledAt;
        update.status = patch.scheduledAt ? "scheduled" : "draft";
      }
      if (patch.estimatedCostBrl !== undefined) update.estimated_cost_brl = patch.estimatedCostBrl;

      const { data, error } = await supabase
        .from("campaigns" as never)
        .update(update as never)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return mapRow(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, clinicId] }),
  });
}
